/**
 * The right Details panel, as real JSX.
 *
 * Part of the embeddable FloorPlanEditor — no host-app chrome (branding,
 * navigation) here, just the inspector for the current room/selection inside
 * the shared PanelShell frame (header actions, scrollable body, hint footer).
 * The data-testid / data-act / data-set attributes are the stable E2E hooks.
 */

import { measureFloorPlanRoom, selectionHint, useFloorPlanEditorState } from '@kaiizen/kai-floor-plan-editor-core';
import { useMemo } from 'react';
import { FigureCards, FlooringSection, PanelShell, RoomSection, SelectionInspector } from './panel';

export interface SidePanelProps {
  /** The editor's save action — when present, the header shows the Save button. */
  onSave?: () => void;
}

export function SidePanel({ onSave }: SidePanelProps): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  const m = useMemo(() => measureFloorPlanRoom(floorPlanEditorState.st.shape), [floorPlanEditorState.st.shape]);
  // The room section and flooring-by-type rows show while NOTHING is selected
  // — the "default inspector"; once something is selected, the selection
  // inspector takes its place (and a whole-room selection shows the same room
  // section again, so the numbers never move).
  const nothingSelected = floorPlanEditorState.st.selection.kind === 'none';
  return (
    <PanelShell
      readOnly={floorPlanEditorState.st.readOnly ?? false}
      canUndo={floorPlanEditorState.canUndo}
      canRedo={floorPlanEditorState.canRedo}
      onUndo={floorPlanEditorState.undo}
      onRedo={floorPlanEditorState.redo}
      onSave={onSave}
      saveDisabled={!floorPlanEditorState.isDirty}
      saveLabel='Save Floor Plan'
      saveTitle={floorPlanEditorState.isDirty ? 'Save this floor plan' : 'No changes since the last save'}
      hint={selectionHint(floorPlanEditorState.st.selection, 'Click')}
    >
      <FigureCards m={m} />
      {nothingSelected
        ? (
          <>
            <RoomSection />
            <FlooringSection m={m} />
          </>
        )
        : <SelectionInspector />}
    </PanelShell>
  );
}
